import React from "react";
import { motion } from "framer-motion";
import { Clock, Flame, Users, ChefHat, Heart, Lightbulb, ShoppingBasket, Share2 } from "lucide-react";
import { recipeImage } from "@/constants/brand";

export function RecipeCard({ recipe, onStartCooking, onSave, saved, onAddToList, onShare }) {
  if (!recipe) return null;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 12 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="mt-3 w-full max-w-[420px] overflow-hidden rounded-3xl border border-espresso/10 bg-white shadow-[0_8px_30px_rgba(44,30,22,0.08)]"
      data-testid="recipe-card"
    >
      <div className="relative h-44 w-full overflow-hidden">
        <img
          src={recipeImage(recipe.category)}
          alt={recipe.title}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-espresso/50 to-transparent" />
        {recipe.difficulty && (
          <span className="absolute right-3 top-3 rounded-full bg-loretta-olive/95 px-3 py-1 text-xs font-semibold text-cream">
            {recipe.difficulty}
          </span>
        )}
        <h3 className="absolute bottom-3 left-4 right-4 font-serif text-2xl font-bold leading-tight text-cream drop-shadow">
          {recipe.title}
        </h3>
      </div>

      <div className="p-5">
        {recipe.description && (
          <p className="mb-4 text-sm italic text-espressolt">{recipe.description}</p>
        )}

        <div className="mb-5 flex flex-wrap gap-4 text-sm text-espressolt">
          {recipe.prep_time && (
            <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-loretta-red" /> {recipe.prep_time}</span>
          )}
          {recipe.cook_time && (
            <span className="flex items-center gap-1.5"><Flame className="h-4 w-4 text-loretta-red" /> {recipe.cook_time}</span>
          )}
          {recipe.servings && (
            <span className="flex items-center gap-1.5"><Users className="h-4 w-4 text-loretta-red" /> {recipe.servings} servings</span>
          )}
        </div>

        {recipe.ingredients?.length > 0 && (
          <div className="mb-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-espresso/50">Ingredients</p>
            <ul className="space-y-1">
              {recipe.ingredients.map((ing, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-espresso/85">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-loretta-olive" />
                  {ing}
                </li>
              ))}
            </ul>
          </div>
        )}

        {recipe.tip && (
          <div className="mb-4 flex items-start gap-2 rounded-2xl bg-creamalt px-4 py-3">
            <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-loretta-red" />
            <p className="text-sm text-espressolt"><span className="font-semibold">Loretta's secret: </span>{recipe.tip}</p>
          </div>
        )}

        <div className="flex gap-2">
          {recipe.steps?.length > 0 && (
            <button
              onClick={() => onStartCooking(recipe)}
              data-testid="start-cooking-button"
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-loretta-red px-5 py-3 font-semibold text-white transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-loretta-redhover"
            >
              <ChefHat className="h-4 w-4" /> Start Cooking
            </button>
          )}
          {onSave && (
            <button
              onClick={() => onSave(recipe)}
              data-testid="save-recipe-button"
              title={saved ? "Saved" : "Save recipe"}
              className={`flex items-center justify-center rounded-full border px-4 py-3 transition-colors duration-200 ${
                saved
                  ? "border-loretta-red bg-loretta-red/10 text-loretta-red"
                  : "border-espresso/15 text-espressolt hover:border-loretta-red hover:text-loretta-red"
              }`}
            >
              <Heart className={`h-4 w-4 ${saved ? "fill-loretta-red" : ""}`} />
            </button>
          )}
        </div>

        {(onAddToList || onShare) && (
          <div className="mt-2 flex gap-2">
            {onAddToList && (
              <button
                onClick={() => onAddToList(recipe)}
                data-testid="add-to-grocery-button"
                className="flex flex-1 items-center justify-center gap-2 rounded-full border border-espresso/15 px-4 py-2.5 text-sm font-medium text-espressolt transition-colors hover:border-loretta-olive hover:text-loretta-olive"
              >
                <ShoppingBasket className="h-4 w-4" /> Add to list
              </button>
            )}
            {onShare && (
              <button
                onClick={() => onShare(recipe)}
                data-testid="share-recipe-button"
                className="flex flex-1 items-center justify-center gap-2 rounded-full border border-espresso/15 px-4 py-2.5 text-sm font-medium text-espressolt transition-colors hover:border-loretta-red hover:text-loretta-red"
              >
                <Share2 className="h-4 w-4" /> Share
              </button>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}
